// Минимальный JWT-декодер БЕЗ верификации подписи.
// Декодирует только payload: подпись валидирует сервер, на клиенте — только
// извлечение claims для UI (sub/tid/role/exp).
//
// Контракт claims — ПЛАТФОРМЕННЫЙ (`app/platform/identity/jwt.py`), а не
// продуктовый: токены выдаёт один identity на все клиенты (Light, Vision),
// поэтому union ролей живёт здесь, в core, и растёт в одном месте.
//
// V1.3: есть ДВЕ формы сессии (bекенд-решение V1.3-a):
//   * tenant-контекстная — payload несёт sub/tid/role/mid/name (Light);
//   * account-only — только sub/name: личность без оптики (пользователь
//     Vision). Поэтому `tid`/`role` в JwtClaims опциональны; наличие
//     tenant-контекста = «оба поля заданы».

interface RawJwtPayload {
  sub?: unknown;
  tid?: unknown;
  role?: unknown;
  exp?: unknown;
  name?: unknown;
  email?: unknown;
}

export interface JwtClaims {
  /** User id (UUID). */
  sub: string;
  /**
   * Tenant id (UUID). Для platform_admin — служебный sentinel-tenant.
   * Отсутствует в account-only сессии (V1.3-a: личность без оптики).
   */
  tid?: string;
  /**
   * Роль. `admin`/`employee` — тенантные роли оптики; `platform_admin` —
   * владелец платформы (кросс-tenant раздел `/platform/*`, M5.4).
   * Отсутствует в account-only сессии (V1.3-a).
   */
  role?: 'admin' | 'employee' | 'platform_admin';
  /** UNIX seconds, expiry. */
  exp: number;
  /**
   * Имя пользователя для юзер-карточки сайдбара. Опционально: токен может его
   * не нести (старые токены / бэкенд ещё не отдаёт claim). Тогда сайдбар
   * показывает роль первой строкой (предусловие №2 брифа сайдбара).
   */
  name?: string;
  /** Email парольной личности (claim `email`), если бэкенд его отдал; у Google-only — пусто. */
  email?: string;
}

function base64UrlDecode(input: string): string {
  // base64-url → base64 + паддинг
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = padded.length % 4;
  const fullyPadded = pad ? padded + '='.repeat(4 - pad) : padded;
  if (typeof atob === 'function') {
    return decodeURIComponent(
      atob(fullyPadded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
  }
  // Node fallback (используется в тестах)
  return Buffer.from(fullyPadded, 'base64').toString('utf8');
}

export function decodeJwt(token: string): JwtClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadSegment = parts[1];
    if (!payloadSegment) return null;
    const json = base64UrlDecode(payloadSegment);
    const raw = JSON.parse(json) as RawJwtPayload;

    if (typeof raw.sub !== 'string') return null;
    if (typeof raw.exp !== 'number') return null;

    // Tenant-контекст: либо оба поля валидны, либо (account-only) оба
    // отсутствуют. Битые значения (tid без role, неизвестная роль) → null:
    // такой токен наш бэкенд не выпускает.
    const name = typeof raw.name === 'string' ? raw.name : undefined;
    const email = typeof raw.email === 'string' && raw.email ? raw.email : undefined;
    if (
      typeof raw.tid === 'string' &&
      (raw.role === 'admin' || raw.role === 'employee' || raw.role === 'platform_admin')
    ) {
      return { sub: raw.sub, tid: raw.tid, role: raw.role, exp: raw.exp, name, email };
    }
    if (raw.tid !== undefined || raw.role !== undefined) return null;
    return { sub: raw.sub, exp: raw.exp, name, email };
  } catch {
    return null;
  }
}
